<template>
    <div class="vrm-modal vrm-modal--wide mfp-with-anim">
        <div class="vrm-modal__header">
            <div class="vrm-modal__title">
                {{ $t('teams.invite_new_members', { i18nFallback: 'Invite new members to team' }) }}
            </div>
        </div>
        <div class="vrm-modal__content">
            <vrm-form @submit="onSubmit" @reset="onReset">
                <div class="vrm-form__fieldset-item vrm-input-table">
                    <label class="sg-textfield-label sg-required">
                        <span class="sg-textfield-label-caption">
                            {{ $t('teams.invitations', { i18nFallback: 'Invitations' }) }}
                        </span>
                    </label>
                    <table class="vrm-table vrm-table--form">
                        <thead>
                            <tr>
                                <th>{{ $t('teams.name', { i18nFallback: 'Name' }) }}</th>
                                <th>{{ $t('common.email', { i18nFallback: 'Email' }) }}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(member, index) in invites" :key="index">
                                <td>
                                    <vrm-text-field
                                        v-model="member.name"
                                        type="text"
                                        class="sg-element"
                                        :checks="[CANNOT_CONTAIN_URL]"
                                        :disabled="busy"
                                    />
                                </td>
                                <td>
                                    <vrm-text-field
                                        v-model="member.email"
                                        type="text"
                                        class="sg-element"
                                        placeholder="name@example.com"
                                        :checks="[VALID_EMAIL_OR_EMPTY]"
                                        :disabled="busy"
                                        :hide-validation-message-on-focus="true"
                                    />
                                </td>
                                <td class="vrm-table__actions">
                                    <span
                                        v-if="index < invites.length - 1"
                                        class="vrm-table__icon--link"
                                        :disabled="busy"
                                        @click="removeInviteRow(index)"
                                    >
                                        <i class="sg-vrm-icon_059_min-round-16px" />
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-if="teams.length" class="vrm-form__fieldset-item">
                    <a
                        class="vrm-form__link"
                        @click.prevent="teamsDropdownVisible = !teamsDropdownVisible"
                    >
                        {{
                            $t('teams.add_users_from_existing_team', {
                                i18nFallback: 'Add users from existing team',
                            })
                        }}
                        <i
                            :class="
                                teamsDropdownVisible
                                    ? 'sg-vrm-icon_059_min-round-16px'
                                    : 'sg-vrm-icon_058_plus-round-16px'
                            "
                        />
                    </a>
                    <vrm-dropdown
                        v-if="teamsDropdownVisible"
                        :options="teams"
                        search-enabled
                        :disabled="busy"
                        @input="addMembersFromTeam"
                    />
                </div>
                <vrm-text-area
                    v-model="personalMessage"
                    :title="$t('teams.invitation_message', { i18nFallback: 'Invitation message' })"
                    :checks="[CANNOT_CONTAIN_URL]"
                />
                <div class="sg-spread">
                    <button type="reset" class="sg-btn sg-site" :disabled="busy" @click="onReset">
                        {{ $t('common.cancel', { i18nFallback: 'Cancel' }) }}
                    </button>
                    <button
                        type="submit"
                        class="sg-btn sg-blue"
                        :class="{
                            'sg-disabled': areInvitesInvalid,
                        }"
                        :disabled="busy || areInvitesInvalid"
                    >
                        {{ $t('teams.add_members', { i18nFallback: 'Add members' }) }}
                    </button>
                </div>
            </vrm-form>
        </div>
        <modal-close-handle />
    </div>
</template>
<style scoped lang="scss"></style>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { ngInject } from '@/vue/decorators/angular-interop';
import {
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATION_TYPE_WARNING,
    NOTIFICATIONS,
    VrmNotification,
} from '@/vue/store/modules/notifications';
import VrmDropdown from '@/vue/components/shared/VrmDropdown';
import ModalCloseHandle from '@/vue/components/modals/ModalCloseHandle';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmTextField from '@/vue/components/forms/VrmTextField';
import VrmTextArea from '@/vue/components/forms/VrmTextArea';
import VrmForm from '@/vue/components/forms/VrmForm';
import { CANNOT_CONTAIN_URL, VALID_EMAIL_OR_EMPTY } from '@/vue/constants/validation-checks';

@Component({
    components: { VrmTextField, VrmTextArea, VrmForm, ModalCloseHandle, VrmDropdown },
})
@AngularLocaleBundle('common')
@AngularLocaleBundle('teams')
export default class UserGroupLinkUsersModal extends Vue {
    CANNOT_CONTAIN_URL = CANNOT_CONTAIN_URL;
    VALID_EMAIL_OR_EMPTY = VALID_EMAIL_OR_EMPTY;
    MAX_INVITES = 10;

    @Prop({ type: Object })
    team;

    invites = [];
    personalMessage = '';
    teamsDropdownVisible = false;
    busy = false;

    /** @type UserGroupsService */
    @ngInject()
    UserGroupsService;

    get teams() {
        return this.$store.state.UserGroupsService.groups.filter(team => {
            return team.idUserGroup !== this.team.idUserGroup;
        });
    }

    created() {
        this.UserGroupsService.loadUserGroups();
    }

    get lastItem() {
        if (!this.invites.length) return null;
        const item = this.invites[this.invites.length - 1];
        return item.name || item.email;
    }

    @Watch('lastItem', { immediate: true })
    onLastItemFilled(value) {
        if ((value && this.invites.length < this.MAX_INVITES) || !this.invites.length) {
            this.invites.push({ name: '', email: '' });
        }
    }

    removeInviteRow(index) {
        if (this.invites.length === this.MAX_INVITES && this.lastItem !== '') {
            this.invites.push({ name: '', email: '' });
        }
        this.invites.splice(index, 1);
    }

    addMembersFromTeam(team) {
        // Remove all empty rows as we might need them
        this.removeEmptyInviteRows();
        // For O(1) check speed we build a hash-set of the email strings
        const emails = new Set(this.invites.map(item => item.email));
        for (const key in team.members) {
            const member = team.members[key];
            if (!emails.has(member.email)) {
                if (this.invites.length >= this.MAX_INVITES) {
                    // Cannot add more
                    this.$store.dispatch(NOTIFICATIONS.ADD, {
                        notification: new VrmNotification({
                            type: NOTIFICATION_TYPE_WARNING,
                            title: this.$t('teams.too_many_invites', {
                                i18nFallback: 'Cannot invite more than ten people at once.',
                            }),
                        }),
                    });
                    break;
                }
                this.invites.push({
                    name: member.name,
                    email: member.email,
                });
                emails.add(member.email); // Prevent duplicate invites
            }
        }
        this.teamsDropdownVisible = false;
    }

    removeEmptyInviteRows() {
        this.invites = this.invites.filter(({ email }) => email.trim() !== '');
    }

    get areInvitesInvalid() {
        return (
            this.validInvites.length === 0 || this.validInvites.length !== this.invites.length - 1
        );
    }
    get validInvites() {
        return this.invites.filter(item => item.email.trim() !== '');
    }

    async onSubmit() {
        if (this.busy) return;
        this.busy = true;
        try {
            const payload = {
                invites: this.validInvites,
                inviteMessage: this.personalMessage,
            };
            if (payload.invites.length) {
                await this.UserGroupsService.inviteUsersToGroup(this.team.idUserGroup, payload);
                this.$store.dispatch(NOTIFICATIONS.ADD, {
                    notification: new VrmNotification({
                        type: NOTIFICATION_TYPE_SUCCESS,
                        title: this.$t('teams.invites_sent', { i18nFallback: 'Invites sent' }),
                    }),
                });
            }
            this.$emit('close');
        } finally {
            this.busy = false;
        }
    }

    onReset() {
        this.$emit('close');
    }
}
</script>
